/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Component, DestroyRef, inject, OnInit } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { FormControl } from '@angular/forms'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { Campus } from 'src/app/models/campus'
import { ConcursoService } from 'src/app/services/concurso.service'
import { RelatorioService } from 'src/app/services/relatorio.service'
import { TipoConcurso } from 'src/app/enums/tipo-concurso'
import { SituacaoInscricao } from 'src/app/enums/situacao-inscricao'
import { ClassificacaoInscricao } from 'src/app/enums/classificacao-inscricao'
import { FiltrosRelatorioCandidatos, RelatorioCandidatos, RelatorioConcursoGrupo } from 'src/app/models/relatorio-candidatos'

/** Tipos de relatório disponíveis (extensível: no futuro, ex.: CONCURSOS). */
type TipoRelatorio = 'CANDIDATOS'

@Component({
  selector: 'app-relatorio',
  templateUrl: './relatorio.component.html',
  styleUrls: ['./relatorio.component.css'],
})
export class RelatorioComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  // Tipo de relatório (dropdown). Hoje só "Candidatos".
  tiposRelatorio: { valor: TipoRelatorio; label: string }[] = [{ valor: 'CANDIDATOS', label: 'Candidatos' }]
  tipoRelatorioFC = new FormControl<TipoRelatorio>('CANDIDATOS')

  // Enums para os dropdowns de filtro.
  tipoConcurso = TipoConcurso
  situacaoInscricao = SituacaoInscricao
  classificacaoInscricao = ClassificacaoInscricao

  // Unidade.
  todasUnidades: Campus = { nome: 'Todas Unidades', uuid: -1 }
  listaDeUnidades: Campus[] = []
  unidadesSelecionadas: Campus[] = []
  unidadeDesabilitada = false
  isMultiunidades = false

  // Filtros.
  concursoFC = new FormControl<number | null>(null)
  tipoConcursoFC = new FormControl<string | null>(null)
  situacaoFC = new FormControl<string | null>(null)
  classificacaoFC = new FormControl<string | null>(null)
  dataInicioFC = new FormControl<Date | null>(null)
  dataFimFC = new FormControl<Date | null>(null)
  pcdFC = new FormControl<boolean | null>(null)
  sinteticoFC = new FormControl<boolean>(false, { nonNullable: true })

  // Dropdown de concursos.
  concursos: { id: number; titulo: string }[] = []
  // Lista exibida no dropdown, filtrada pela busca textual.
  concursosFiltrados: { id: number; titulo: string }[] = []
  concursoFiltroFC = new FormControl<string>('', { nonNullable: true })

  // Resultado.
  relatorio: RelatorioCandidatos | null = null
  carregando = false
  gerandoPdf = false

  // Visão da apresentação: 'cards' (por concurso) ou 'master' (tabela consolidada).
  visao: 'cards' | 'master' = 'cards'

  constructor(
    private concursoService: ConcursoService,
    private relatorioService: RelatorioService,
    private unespCoreAuthService: UnespCoreAuthService,
    private unespCoreMessageService: UnespCoreMessageService
  ) {}

  ngOnInit(): void {
    this.isMultiunidades = this.unespCoreAuthService.hasPermissionBasedRoles(['MULTIUNIDADES'])

    this.concursoService
      .camposHabilitados()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(unidades => {
        this.listaDeUnidades = unidades ?? []
        // Regra de unidade por perfil:
        // - MULTIUNIDADES: pode escolher ("Todas" disponível).
        // - Demais: unidade travada na(s) habilitada(s), sem troca.
        if (!this.isMultiunidades) {
          this.unidadesSelecionadas = [...this.listaDeUnidades]
          this.unidadeDesabilitada = true
        } else {
          this.listaDeUnidades = [this.todasUnidades, ...this.listaDeUnidades]
        }
      })

    this.concursoService
      .listarTodos()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lista => {
        this.concursos = lista ?? []
        this.concursosFiltrados = this.concursos
      })

    // Busca textual no seletor de concurso (filtra a lista exibida no dropdown).
    this.concursoFiltroFC.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(termo => this.filtrarConcursos(termo))
  }

  /** Filtra a lista de concursos exibida no dropdown pelo termo digitado (case-insensitive). */
  private filtrarConcursos(termo: string): void {
    const t = (termo ?? '').trim().toLowerCase()
    this.concursosFiltrados = t
      ? this.concursos.filter(c => c.titulo.toLowerCase().includes(t))
      : this.concursos
  }

  /** Monta o payload de filtros a partir dos controles. */
  private montarFiltros(): FiltrosRelatorioCandidatos {
    const unidades = this.unidadesSelecionadas
      .map(u => u.uuid)
      .filter(uuid => uuid !== this.todasUnidades.uuid)

    return {
      unidades,
      concursoId: this.concursoFC.value ?? null,
      tipoConcurso: this.tipoConcursoFC.value || null,
      situacao: this.situacaoFC.value || null,
      classificacao: this.classificacaoFC.value || null,
      dataInicio: this.formatarData(this.dataInicioFC.value),
      dataFim: this.formatarData(this.dataFimFC.value),
      pcd: this.pcdFC.value,
      sintetico: this.sinteticoFC.value,
    }
  }

  private formatarData(data: Date | null): string | null {
    if (!data) return null
    const ano = data.getFullYear()
    const mes = String(data.getMonth() + 1).padStart(2, '0')
    const dia = String(data.getDate()).padStart(2, '0')
    return `${ano}-${mes}-${dia}`
  }

  consultar(): void {
    this.carregando = true
    this.relatorioService
      .gerarRelatorioCandidatos(this.montarFiltros())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: relatorio => {
          this.relatorio = relatorio
          this.carregando = false
        },
        error: (erro: unknown) => {
          // Limpa o resultado anterior para não exibir dados defasados junto do erro.
          this.relatorio = null
          this.carregando = false
          this.unespCoreMessageService.showMessageError(this.mensagemErro(erro))
        },
      })
  }

  gerarPdf(): void {
    this.gerandoPdf = true
    this.relatorioService
      .gerarRelatorioCandidatosPdf(this.montarFiltros())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: mensagem => {
          this.gerandoPdf = false
          this.unespCoreMessageService.showMessageSuccess(
            mensagem || "O relatório será enviado para o seu e-mail."
          )
        },
        error: (erro: unknown) => {
          this.gerandoPdf = false
          this.unespCoreMessageService.showMessageError(this.mensagemErro(erro))
        },
      })
  }

  /**
   * Extrai uma mensagem amigável de um erro HTTP. Usa a mensagem retornada pelo backend
   * (ex.: 400 com "Usuário sem e-mail...") quando disponível; caso contrário, usa um texto padrão.
   */
  private mensagemErro(erro: unknown): string {
    const padrao = 'Não foi possível concluir a operação. Tente novamente em alguns instantes.'
    if (erro instanceof HttpErrorResponse) {
      const corpo = erro.error
      if (typeof corpo === 'string' && corpo.trim()) return corpo
      if (corpo && typeof corpo === 'object') {
        const obj = corpo as Record<string, unknown>
        const msg = obj['message'] ?? obj['mensagemUsuario'] ?? obj['error']
        if (typeof msg === 'string' && msg.trim()) return msg
      }
    }
    return padrao
  }

  limparFiltros(): void {
    this.concursoFC.reset()
    this.tipoConcursoFC.reset()
    this.situacaoFC.reset()
    this.classificacaoFC.reset()
    this.dataInicioFC.reset()
    this.dataFimFC.reset()
    this.pcdFC.reset()
    this.sinteticoFC.setValue(false)
    if (this.isMultiunidades) this.unidadesSelecionadas = []
  }

  comparaUnidade(a: Campus, b: Campus): boolean {
    return a?.uuid === b?.uuid
  }

  /** Decide se a coluna de Autodeclaração deve aparecer para o concurso (apenas PTPP). */
  ehPtpp(grupo: RelatorioConcursoGrupo): boolean {
    return grupo.tipoConcursoSigla === 'PTPP'
  }

  /** Converte boolean/null em texto amigável para a tela. */
  simNao(valor: boolean | null): string {
    if (valor === null || valor === undefined) return '—'
    return valor ? 'Sim' : 'Não'
  }

  /** Alterna entre a visão por concurso (cards) e a tabela consolidada (mestre). */
  trocarVisao(visao: 'cards' | 'master'): void {
    this.visao = visao
  }

  /**
   * Totais gerais para o dashboard (KPIs). O backend só envia totalGeral/inscritosGeral quando há
   * mais de um concurso (exibirTotalGeral). Para exibir os KPIs mesmo com um único concurso,
   * consolida a partir dos grupos quando necessário.
   */
  get totaisDashboard(): {
    aprovados: number; reprovados: number; naoAvaliados: number; total: number;
    deferidosTotal: number; deferidosPcd: number; deferidosAmpla: number;
  } | null {
    if (!this.relatorio || this.relatorio.concursos.length === 0) return null
    const r = this.relatorio
    if (r.exibirTotalGeral) {
      return {
        aprovados: r.totalGeral.aprovados,
        reprovados: r.totalGeral.reprovados,
        naoAvaliados: r.totalGeral.naoAvaliados,
        total: r.totalGeral.total,
        deferidosTotal: r.inscritosGeral.total,
        deferidosPcd: r.inscritosGeral.pcd,
        deferidosAmpla: r.inscritosGeral.ampla,
      }
    }
    // Um único concurso: usa os totais do próprio grupo.
    const g = r.concursos[0]
    return {
      aprovados: g.totais.aprovados,
      reprovados: g.totais.reprovados,
      naoAvaliados: g.totais.naoAvaliados,
      total: g.totais.total,
      deferidosTotal: g.inscritos.total,
      deferidosPcd: g.inscritos.pcd,
      deferidosAmpla: g.inscritos.ampla,
    }
  }

  /** Percentual (string com 2 casas) de parte sobre o total; vazio se total 0. */
  percentual(parte: number, total: number): string {
    if (!total) return '0,00%'
    return (100 * parte / total).toFixed(2).replace('.', ',') + '%'
  }

  // ===========================================================================
  // Exportação (CSV ;) e cópia (TSV) — apenas TOTAIS e LISTAGENS, nunca o dashboard.
  // Analítico: concursos + candidatos + totais. Sintético: concursos + totais.
  // ===========================================================================

  /** Cabeçalhos das colunas de candidato (analítico). */
  private readonly colunasCandidato = [
    'Processo', 'Concurso', 'Tipo', 'Unidade/Campus', 'ID da inscrição', 'Nome',
    'PCD', 'Autodeclaração', 'Data inscrição', 'Situação', 'Motivo situação',
    'Classificação', 'Motivo classificação', 'Posição final',
  ]

  /** Monta a matriz de dados (linhas x colunas) do relatório, conforme o modo. */
  private montarMatriz(): string[][] {
    const r = this.relatorio
    if (!r || r.concursos.length === 0) return []
    const linhas: string[][] = []

    if (r.sintetico) {
      // Sintético: apenas concursos + totais.
      linhas.push([
        'Processo', 'Concurso', 'Tipo', 'Unidade/Campus', 'Período', 'Abertura',
        'Resultado final', 'Duração (dias)',
        'Aprovados', 'Reprovados', 'Não avaliados', 'Total (todas as inscrições)',
        'Inscritos deferidos', 'Deferidos PCD', 'Deferidos ampla',
      ])
      for (const g of r.concursos) {
        linhas.push(this.linhaConcursoTotais(g))
      }
    } else {
      // Analítico: listagem de candidatos por concurso + totais por concurso.
      for (const g of r.concursos) {
        linhas.push([`Concurso: ${g.titulo}`])
        linhas.push([
          `Processo: ${g.processo ?? '—'}`, `Tipo: ${g.tipoConcurso ?? '—'}`,
          `Unidade/Campus: ${this.unidadeCampus(g)}`, `Período: ${g.periodo ?? '—'}`,
          `Abertura: ${this.dataBr(g.dataAbertura)}`,
          `Resultado final: ${g.concursoFinalizado ? this.dataBr(g.dataResultadoFinal) : '—'}`,
          `Duração (dias): ${g.concursoFinalizado && g.diasDuracao != null ? g.diasDuracao : '—'}`,
        ])
        linhas.push(this.colunasCandidato)
        for (const c of g.candidatos) {
          linhas.push([
            g.processo ?? '', g.titulo ?? '', g.tipoConcurso ?? '', this.unidadeCampus(g),
            String(c.inscricaoId ?? ''), c.nome ?? '',
            this.simNao(c.pcd),
            this.ehPtpp(g) ? (c.autodeclaracao ?? '—') : 'N/A',
            this.dataBr(c.dataInscricao), c.situacao ?? '', c.situacaoMotivo ?? '',
            c.classificacao ?? '', c.classificacaoMotivo ?? '', c.posicaoFinal ?? '—',
          ])
        }
        // Totais do concurso.
        linhas.push([
          'Totais do concurso',
          `Aprovados: ${g.totais.aprovados}`, `Reprovados: ${g.totais.reprovados}`,
          `Não avaliados: ${g.totais.naoAvaliados}`, `Total (todas as inscrições): ${g.totais.total}`,
          `Inscritos deferidos: ${g.inscritos.total}`, `Deferidos PCD: ${g.inscritos.pcd}`,
          `Deferidos ampla: ${g.inscritos.ampla}`,
        ])
        linhas.push([]) // separador entre concursos
      }
    }

    // Total geral (apenas quando houver mais de um concurso).
    if (r.exibirTotalGeral) {
      linhas.push([
        'TOTAL GERAL',
        `Aprovados: ${r.totalGeral.aprovados}`, `Reprovados: ${r.totalGeral.reprovados}`,
        `Não avaliados: ${r.totalGeral.naoAvaliados}`, `Total (todas as inscrições): ${r.totalGeral.total}`,
        `Inscritos deferidos: ${r.inscritosGeral.total}`, `Deferidos PCD: ${r.inscritosGeral.pcd}`,
        `Deferidos ampla: ${r.inscritosGeral.ampla}`,
      ])
    }
    return linhas
  }

  private linhaConcursoTotais(g: RelatorioConcursoGrupo): string[] {
    return [
      g.processo ?? '', g.titulo ?? '', g.tipoConcurso ?? '', this.unidadeCampus(g),
      g.periodo ?? '', this.dataBr(g.dataAbertura),
      g.concursoFinalizado ? this.dataBr(g.dataResultadoFinal) : '—',
      g.concursoFinalizado && g.diasDuracao != null ? String(g.diasDuracao) : '—',
      String(g.totais.aprovados), String(g.totais.reprovados), String(g.totais.naoAvaliados),
      String(g.totais.total),
      String(g.inscritos.total), String(g.inscritos.pcd), String(g.inscritos.ampla),
    ]
  }

  private unidadeCampus(g: RelatorioConcursoGrupo): string {
    return g.campus ? `${g.unidadeSigla} / ${g.campus}` : (g.unidadeSigla ?? '')
  }

  private dataBr(iso: string | null): string {
    if (!iso) return '—'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    const dia = String(d.getDate()).padStart(2, '0')
    const mes = String(d.getMonth() + 1).padStart(2, '0')
    return `${dia}/${mes}/${d.getFullYear()}`
  }

  /** Escapa um campo para CSV com separador ';'. */
  private escaparCsv(valor: string): string {
    const v = valor ?? ''
    if (/[";\n\r]/.test(v)) return '"' + v.replace(/"/g, '""') + '"'
    return v
  }

  /** Exporta o relatório (totais + listagens) como CSV (separador ';', BOM UTF-8). */
  exportarCsv(): void {
    const matriz = this.montarMatriz()
    if (matriz.length === 0) {
      this.unespCoreMessageService.showMessageWarning('Não há dados para exportar.')
      return
    }
    const csv = matriz.map(l => l.map(c => this.escaparCsv(c)).join(';')).join('\r\n')
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `relatorio-candidatos-${this.relatorio?.sintetico ? 'sintetico' : 'analitico'}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
    this.unespCoreMessageService.showMessageSuccess('CSV exportado com sucesso.')
  }

  /** Copia o relatório (totais + listagens) como TSV para colar no Excel. */
  copiarParaExcel(): void {
    const matriz = this.montarMatriz()
    if (matriz.length === 0) {
      this.unespCoreMessageService.showMessageWarning('Não há dados para copiar.')
      return
    }
    const tsv = matriz.map(l => l.join('\t')).join('\n')
    this.copiarTexto(tsv)
  }

  /** Copia texto para a área de transferência (Clipboard API com fallback execCommand). */
  private copiarTexto(texto: string): void {
    const sucesso = () =>
      this.unespCoreMessageService.showMessageSuccess('Conteúdo copiado. Cole no Excel (Ctrl + V).')
    const falha = () =>
      this.unespCoreMessageService.showMessageError('Não foi possível copiar o conteúdo.')

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(texto).then(sucesso).catch(() => this.copiarFallback(texto, sucesso, falha))
    } else {
      this.copiarFallback(texto, sucesso, falha)
    }
  }

  private copiarFallback(texto: string, ok: () => void, erro: () => void): void {
    const ta = document.createElement('textarea')
    ta.value = texto
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy') ? ok() : erro()
    } catch {
      erro()
    } finally {
      document.body.removeChild(ta)
    }
  }
}
